import React from 'react';
import styles from './PromptPanel.module.css';
import { type Instruction } from '../AssistantView/AssistantViewController';
import { ApplicationView } from '@aha-app/mvc';
import classNames from 'classnames';

const DefaultScopeItem = ApplicationView(
  ({
    content,
    onRemove,
    className,
    onClick,
  }: {
    content: string | React.ReactNode;
    onRemove?: () => void;
    className?: string;
    onClick?: (e) => void;
  }) => {
    if (!content) return null;

    const onRemoveClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onRemove?.();
    };

    return (
      <div
        className={classNames(styles.scopeItem, className, {
          [styles.scopeItemClickable]: !!onClick,
        })}
        onClick={onClick}
      >
        <div className={styles.scopeItemText}>{content}</div>
        {onRemove && <i className='fa fa-xmark' onClick={onRemoveClick} />}
      </div>
    );
  }
);

const SelectionScopeItem = ApplicationView(
  ({ content }: { content: string }) => {
    return (
      <DefaultScopeItem
        content={content}
        className={styles.scopeItemSelection}
      />
    );
  }
);

const InstructionScopeItem = ApplicationView(
  ({
    instructions,
    onRemove,
    onClick,
  }: {
    instructions: Array<Instruction>;
    onRemove: (i: Instruction) => void;
    onClick: (i: Instruction) => void;
  }) => {
    if (!instructions || instructions.length === 0) return null;

    return instructions.map(instruction => (
      <DefaultScopeItem
        content={
          <>
            <i className='fa-regular fa-memo' /> {instruction.name}
          </>
        }
        onRemove={() => onRemove(instruction)}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClick(instruction);
        }}
      />
    ));
  }
);

const ReferenceScopeItem = ApplicationView(
  ({
    referenceNum,
    onRemove,
    toggleArtifact,
  }: {
    referenceNum: string;
    onRemove?: () => void;
    toggleArtifact?: (referenceNum: string) => void;
  }) => {
    if (!referenceNum) return null;

    let name = referenceNum;
    let icon: string | null = null;
    let onClick: ((e: React.MouseEvent) => void) | undefined;

    if (referenceNum.startsWith('Report-')) {
      const reportName = document
        .querySelector('.report-header h1')
        ?.textContent?.trim();
      if (reportName) {
        name = reportName;
      }
      icon = 'aha-icon aha-icon-roadmap';
    } else if (referenceNum.startsWith('Artifact-')) {
      name = 'Artifact';
      icon = 'far fa-file-lines';
      onClick = e => {
        e.preventDefault();
        e.stopPropagation();
        toggleArtifact?.(referenceNum);
      };
    }

    return (
      <DefaultScopeItem
        content={
          <>
            <aha-record-icon
              icon-override={icon}
              reference-num={referenceNum}
            />{' '}
            {name}
          </>
        }
        onRemove={onRemove}
        onClick={onClick}
      />
    );
  }
);

export {
  DefaultScopeItem,
  SelectionScopeItem,
  InstructionScopeItem,
  ReferenceScopeItem,
};
